import { useState, useEffect, useRef } from 'react'
import { ShoppingCart, RefreshCw, Bell } from 'lucide-react'
import { reservationService, Reservation } from '@services/reservationService'

const STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  pending: { label: 'En attente', color: 'text-yellow-800', bgColor: 'bg-yellow-100' },
  confirmed: { label: 'Confirmée', color: 'text-green-800', bgColor: 'bg-green-100' },
  preparing: { label: 'En préparation', color: 'text-blue-800', bgColor: 'bg-blue-100' },
  ready: { label: 'Prête', color: 'text-purple-800', bgColor: 'bg-purple-100' },
  completed: { label: 'Complétée', color: 'text-green-800', bgColor: 'bg-green-200' },
  cancelled: { label: 'Annulée', color: 'text-red-800', bgColor: 'bg-red-100' },
  expired: { label: 'Expirée', color: 'text-gray-800', bgColor: 'bg-gray-100' }
}

const LiveOrdersWidget = () => {
  const [orders, setOrders] = useState<Reservation[]>([])
  const [loading, setLoading] = useState(true)
  const [wsConnected, setWsConnected] = useState(false)
  const [newOrderAlert, setNewOrderAlert] = useState(false)
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const loadOrders = async () => {
    try {
      setLoading(true)
      const response = await reservationService.getAll({})
      if (response.success) {
        setOrders(response.data || [])
      }
    } catch (err) {
      console.warn('Failed to load orders:', err)
    } finally {
      setLoading(false)
    }
  }

  // Connect to WebSocket for real-time updates
  useEffect(() => {
    const hostname = window.location.hostname;
    const port = window.location.port;
    const wsUrl = `ws://${hostname}${port ? ':' + port : ''}/api/reservations?user_id=admin`

    const connectWs = () => {
      try {
        const ws = new WebSocket(wsUrl)
        wsRef.current = ws

        ws.onopen = () => {
          setWsConnected(true)
        }

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data)
            if (data.type === 'reservation.status.updated') {
              // Update the order in the list
              setOrders(prev => {
                const existing = prev.find(o => o.reservation_id === data.reservation_id)
                if (existing) {
                  return prev.map(o =>
                    o.reservation_id === data.reservation_id
                      ? { ...o, status: data.status, updated_at: new Date().toISOString() }
                      : o
                  )
                }
                return prev
              })

              // Show alert for new pending orders
              if (data.status === 'pending') {
                setNewOrderAlert(true)
                setTimeout(() => setNewOrderAlert(false), 5000)
              }
            }
          } catch {
            // Ignore non-JSON messages
          }
        }

        ws.onclose = () => {
          setWsConnected(false)
          reconnectTimeoutRef.current = setTimeout(connectWs, 5000)
        }

        ws.onerror = () => {
          ws.close()
        }
      } catch {
        setWsConnected(false)
      }
    }

    connectWs()

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }
      if (wsRef.current) {
        wsRef.current.close()
      }
    }
  }, [])

  useEffect(() => {
    loadOrders()
  }, [])

  const getStatusStyle = (status: string) => {
    return STATUS_CONFIG[status] || { label: status, color: 'text-gray-800', bgColor: 'bg-gray-100' }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getItemName = (order: Reservation) => {
    if (order.product_id) return `Produit #${order.product_id}`
    if (order.offer_id) return `Offre #${order.offer_id}`
    return 'Article'
  }

  const activeOrders = orders.filter(o => 
    o.status === 'pending' || o.status === 'confirmed'
  )

  return (
    <div className="card">
      <div className="card-header">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <ShoppingCart className="h-5 w-5 text-primary-500 mr-2" />
            Commandes en direct
            {activeOrders.length > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-800 rounded-full">
                {activeOrders.length} active{activeOrders.length > 1 ? 's' : ''}
              </span>
            )}
          </h2>
          <div className="flex items-center space-x-2">
            {/* WebSocket indicator */}
            <div className="flex items-center">
              <div className={`h-2 w-2 rounded-full ${wsConnected ? 'bg-green-500' : 'bg-gray-300'}`} />
              <span className="ml-1 text-xs text-gray-400">
                {wsConnected ? 'Connecté' : 'Déconnecté'}
              </span>
            </div>
            <button
              onClick={loadOrders}
              className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
              title="Actualiser"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>
      <div className="card-body p-0">
        {/* New order alert */}
        {newOrderAlert && (
          <div className="mx-4 mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center animate-pulse">
            <Bell className="h-4 w-4 text-blue-500 mr-2" />
            <span className="text-sm text-blue-700 font-medium">Nouvelle commande reçue !</span>
          </div>
        )}

        {loading ? (
          <div className="text-center py-8 text-gray-500">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mb-2"></div>
            <p className="text-sm">Chargement...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <ShoppingCart className="h-10 w-10 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">Aucune commande</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
            {orders.slice(0, 20).map((order) => {
              const statusStyle = getStatusStyle(order.status)
              return (
                <div key={order.reservation_id} className="flex items-start px-4 py-3 hover:bg-gray-50 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {getItemName(order)}
                      </p>
                      <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${statusStyle.bgColor} ${statusStyle.color}`}>
                        {statusStyle.label}
                      </span>
                    </div>
                    <div className="flex items-center mt-1 text-xs text-gray-500 space-x-3">
                      <span>{order.user_first_name || order.user_last_name ? `${order.user_first_name || ''} ${order.user_last_name || ''}`.trim() : 'Client'}</span>
                      <span>{order.merchant_name || 'N/D'}</span>
                      <span>{formatDate(order.created_at)}</span>
                    </div>
                    <div className="flex items-center mt-1 text-xs text-gray-400">
                      <span className="font-medium">{Number(order.total_amount).toFixed(2)} €</span>
                      <span className="mx-1">·</span>
                      <span>Qty: {order.quantity}</span>
                      <span className="mx-1">·</span>
                      <span>#{order.reservation_id.slice(0, 8)}</span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default LiveOrdersWidget
